import { Account } from 'crono-fe-common/types/account';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import { LogCall } from 'crono-fe-common/types/logCall';
import { Event } from 'crono-fe-common/types/event';
import { Task } from 'crono-fe-common/types/task';
import { ConversationMessage } from 'crono-fe-common/types/ConversationMessage';
import { Prospect } from 'crono-fe-common/types/prospect';
import { Opportunity } from 'crono-fe-common/types/DTO/opportunityViewResponseDTO';
import type { Dispatch, SetStateAction } from 'react';
import type { LinkedinTabLabel } from 'pages/accountDrawer/linkedinView/index';

export interface AccountDrawerContextType {
  account: Account | null;
  selectedAccountId: string | null;
  selectedProspectId: string | null;
  isLoadingGetAccount: boolean;
  currentAccountNotExisting: boolean;

  drawerContent: DrawerContent | null;
  closeDrawer: (options?: { force?: boolean }) => void;
  handleRemoveModal: () => void;
  updateDrawerContent: (partial: Partial<DrawerContent>) => void;
  openContactDrawer: (data: OpenContactDrawerData) => void;
  openAccountDrawer: (data: OpenAccountDrawerData) => void;
  openCallLogDrawer: (data: OpenCallLogSideTab) => void;
  openDealDrawer: (data: OpenDealDrawerData) => void;
  openActivityLogDrawer: (data: OpenActivityLogDrawerData) => void;
  openTaskDrawer: (data: OpenTaskDrawerData) => void;
  openTaskExecutionDrawer: (data: OpenTaskExecutionDrawerData) => void;
  openMeetingDrawer: (data: OpenMeetingDrawerData) => void;

  callNeedsDiscardConfirm: boolean;
  setCallViewIsDirty: (isDirty: boolean) => void;
  openDiscardCallModal: (params?: { onConfirm?: () => void }) => void;

  /** Split drawer's body — null until it has mounted. */
  taskSplitPortalNode: HTMLDivElement | null;

  /** Lifted from LinkedinView so the split drawer's header can show it too. */
  linkedinTabLabel: LinkedinTabLabel;
  setLinkedinTabLabel: Dispatch<SetStateAction<LinkedinTabLabel>>;
}

/**
 * Stable drawer-opening actions. Each function's identity only changes when
 * the call-discard-guard state changes (rare), not on every account-query
 * refetch or drawerContent update — safe for components rendered many times
 * (table rows) to subscribe to without inheriting unrelated re-renders.
 */
export interface AccountDrawerOpenersContextType {
  closeDrawer: (options?: { force?: boolean }) => void;
  openContactDrawer: (data: OpenContactDrawerData) => void;
  openAccountDrawer: (data: OpenAccountDrawerData) => void;
  openCallLogDrawer: (data: OpenCallLogSideTab) => void;
  openDealDrawer: (data: OpenDealDrawerData) => void;
  openActivityLogDrawer: (data: OpenActivityLogDrawerData) => void;
  openTaskDrawer: (data: OpenTaskDrawerData) => void;
  openMeetingDrawer: (data: OpenMeetingDrawerData) => void;
}

export interface AccountDrawerSelectionContextType {
  selectedAccountId: string | null;
  selectedProspectId: string | null;
}

export interface AccountDrawerAccountContextType {
  account: Account | null;
  isLoadingGetAccount: boolean;
  currentAccountNotExisting: boolean;
}

export interface AccountDrawerCallGuardContextType {
  callNeedsDiscardConfirm: boolean;
  setCallViewIsDirty: (isDirty: boolean) => void;
  openDiscardCallModal: (params?: { onConfirm?: () => void }) => void;
}

interface OpenDrawerBaseData {
  accountId: string;
}

export interface OpenAccountDrawerData extends OpenDrawerBaseData {
  forceReload?: boolean;
  initialTab?: DrawerTab;
  openScore?: boolean;
}

export interface OpenContactDrawerData extends OpenDrawerBaseData {
  prospectId?: string | null;
  openScore?: boolean;
  tab?: DrawerTab;
  forceReload?: boolean;
}

/** A call known only by its log id; CallLogView fetches the rest. */
export interface CallLogRef {
  callLog: Pick<LogCall, 'id'>;
  user?: string | null;
}

export interface OpenCallLogSideTab extends OpenDrawerBaseData {
  prospectId?: string | null;
  activity?:
    | Event
    | Task
    | (ConversationMessage & { callLog: LogCall })
    | CallLogRef
    | null;
}

export interface OpenDealDrawerData extends OpenDrawerBaseData {
  opportunityId: string;
  forceReload?: boolean;
}

export interface OpenActivityLogDrawerData extends OpenDrawerBaseData {
  prospectId?: string | null;
  activity?: Event | Task | null;
  logType: 'callLog' | 'genericLog';
}

export interface OpenMeetingDrawerData extends OpenDrawerBaseData {
  prospectId?: string | null;
  activity: Event;
}

export interface OpenTaskDrawerData extends OpenDrawerBaseData {
  prospectId?: string | null;
  taskType: 'email' | 'linkedin' | 'call' | 'inmail';
  initialPhoneSelected?: PhoneOption;
}

export interface OpenTaskExecutionDrawerData extends OpenDrawerBaseData {
  prospectId?: string | null;
  task: TaskTodo;
  tab: DrawerTab;
  callOnSuccess?: (...args: unknown[]) => void;
  defaultOpenTemplateTab?: boolean;
  forceReload?: boolean;
}

export type DrawerTab =
  | 'account'
  | 'prospect'
  | 'email'
  | 'linkedin'
  | 'call'
  | 'callLog'
  | 'inmail'
  | 'deals'
  | 'generic'
  | 'genericLog'
  | 'meeting'
  | 'timeline';

/** Tabs opened via openTaskDrawer — an active one means "a task is in progress". */
const TASK_MODAL_TABS: DrawerTab[] = ['email', 'linkedin', 'call', 'inmail'];

export function isTaskModalTab(tab?: DrawerTab): boolean {
  return !!tab && TASK_MODAL_TABS.includes(tab);
}

export function parseTaskType(type: TaskTodoType): DrawerTab {
  switch (type) {
    case TaskTodoType.EMAIL:
      return 'email';
    case TaskTodoType.LINKEDIN:
      return 'linkedin';
    case TaskTodoType.CALL:
      return 'call';
    case TaskTodoType.IN_MAIL:
      return 'inmail';
    case TaskTodoType.GENERIC:
      return 'generic';
    default:
      return 'account';
  }
}

const TAB_VALUE_TO_SIDE_TAB: DrawerTab[] = [
  'account',
  'prospect',
  'timeline',
  'email',
  'linkedin',
  'call',
  'callLog',
  'deals',
  'inmail',
  'generic',
  'genericLog',
  'meeting',
];

export function parseTabValue(tabValue: number): DrawerTab {
  return TAB_VALUE_TO_SIDE_TAB[tabValue] ?? 'account';
}

export function parseTab(tab: DrawerTab | undefined) {
  switch (tab) {
    case 'account':
      return 0;
    case 'prospect':
      return 1;
    case 'timeline':
      return 2;
    case 'email':
      return 3;
    case 'linkedin':
      return 4;
    case 'call':
      return 5;
    case 'callLog':
      return 6;
    case 'deals':
      return 7;
    case 'inmail':
      return 8;
    case 'generic':
      return 9;
    case 'genericLog':
      return 10;
    case 'meeting':
      return 11;
    default:
      return 0;
  }
}

export type DrawerContent = {
  tab?: DrawerTab;
  selectedProspectId?: string;
  modalVisible?: DrawerTab;
  openScore?: boolean;
  task?: TaskTodo | null;
  forceReload?: number;
  /** Bumped on every openTaskDrawer call — lets task views (e.g. CallView) key
   *  off it to remount and reset their internal state when a new task starts,
   *  even for the same tab/prospect (e.g. a fresh call to a contact just
   *  called). */
  taskSessionId?: number;
  selectedThreadId?: {
    threadId: string | null;
    toAll: boolean;
    senderEmail: string;
  } | null;
  callOnSuccess?: () => void;
  suggestionId?: number | null;
  defaultOpenTemplateTab?: boolean;
  activity?:
    | Task
    | Event
    | (ConversationMessage & { callLog: LogCall })
    | CallLogRef
    | null;
  selectedOpportunityId?: string | null;
  initialPhoneSelected?: PhoneOption;
  /** Only meaningful while modalVisible is a task tab — see isTaskModalTab. */
  isTaskSplitView?: boolean;

  accountId?: string;

  /** Currently visible prospect in the list, the one on top */
  activeProspect?: Prospect | null;
  /** Currently visible opportunity in the list, the one on top */
  activeOpportunity?: Opportunity | null;
};

export type PhoneOption = 'phone' | 'mobilePhone' | 'accountPhone';
